import React from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ITaxInvoice } from '@/interfaces/report';
import { ExportOutlined } from '@ant-design/icons';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { useSetting } from '@/contexts/CompanyContext';
import { sumBy } from 'lodash';

interface IProps {
  date: Date | string | Dayjs | null | undefined;
  data: ITaxInvoice[];
}

const ExcelExport = ({ data, date }: IProps) => {
  const { setting } = useSetting();
  const handleExport = async () => {
    // Create a new workbook and add a worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('TaxInvoice');
    worksheet.getColumn('A').width = 5;
    worksheet.getColumn('B').width = 15;
    worksheet.getColumn('C').width = 15;
    worksheet.getColumn('D').width = 50;
    worksheet.getColumn('E').width = 20;
    worksheet.getColumn('F').width = 15;
    worksheet.getColumn('G').width = 15;
    worksheet.getColumn('H').width = 15;
    worksheet.getColumn('I').width = 15;
    worksheet.mergeCells('A1:I1');
    worksheet.getCell('A1').value = 'รายงานภาษีขาย';
    worksheet.getCell('A1').alignment = {
      horizontal: 'center',
      vertical: 'middle',
    };
    worksheet.getCell('A1').font = {
      bold: true,
      color: { argb: '000000' },
      size: 18,
    };

    worksheet.getRow(2).height = 30;
    worksheet.getCell('A2').value = 'เดือนภาษี';
    worksheet.mergeCells('C2:D2');
    worksheet.getCell('D2').value = dayjs(date).format('MMMM');
    worksheet.getCell('E2').value = 'ปี';
    worksheet.mergeCells('G2:H2');
    worksheet.getCell('G2').value = dayjs(date).format('BBBB');
    worksheet.getRow(3).height = 30;
    worksheet.getCell('A3').value = 'ชื่อผู้ประกอบการ';
    worksheet.getCell('C3').value = 'บริษัท อัลไลด์ อโกร จำกัด';
    worksheet.getCell('E3').value = 'เลขประจำผู้เสียภาษีอากร';
    worksheet.getCell('G3').value = setting?.companyTaxId;
    worksheet.getRow(4).height = 30;
    worksheet.getCell('A4').value = 'ชื่อสถานประกอบการ';
    worksheet.getCell('C4').value = 'บริษัท อัลไลด์ อโกร จำกัด';
    worksheet.getCell('E4').value = 'สำนักงานใหญ่';
    worksheet.getRow(6);
    worksheet.mergeCells('A6:A8');
    worksheet.mergeCells('B6:C6');
    worksheet.mergeCells('B7:B8');
    worksheet.mergeCells('C7:C8');
    worksheet.mergeCells('D6:D8');
    worksheet.mergeCells('E6:E8');
    worksheet.mergeCells('F6:G6');
    worksheet.mergeCells('F7:F8');
    worksheet.mergeCells('G7:G8');
    worksheet.mergeCells('H6:H8');
    worksheet.mergeCells('I6:I8');

    worksheet.getCell('A6').value = 'ลำดับที่';
    worksheet.getCell('B6').value = 'ใบกำกับภาษี';
    worksheet.getCell('D6').value = 'ชื่อผู้ซื้อสินค้า/ผู้รับบริการ';
    worksheet.getCell('E6').value =
      'เลขประจำตัวผู้เสียภาษีอากรของผู้ซื้อสินค้า/ผู้รับบริการ';
    worksheet.getCell('F6').value = 'สถานประกอบการ';
    worksheet.getCell('H6').value = 'มูลค่าสินค้าหรือบริการ';
    worksheet.getCell('I6').value = 'จำนวนเงินภาษีมูลค่าเพิ่ม';

    worksheet.getCell('B7').value = 'วัน เดือน ปี';
    worksheet.getCell('C7').value = 'เลขที่';
    worksheet.getCell('F7').value = 'สำนักงานใหญ่';
    worksheet.getCell('G7').value = 'สาขาที่';

    // worksheet.getRow(6).values = []; // Empty row for merging structure

    // Apply alignment & styling to header rows
    for (let rowNum = 6; rowNum <= 8; rowNum++) {
      const row = worksheet.getRow(rowNum);
      row.eachCell(cell => {
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle',
          wrapText: true,
        };
        cell.font = { bold: true };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    }

    // Add data to the worksheet
    data.forEach((item: ITaxInvoice, index: number) => {
      const row = worksheet.addRow([
        index + 1,
        formatDate(dayjs(item.orderDate)),
        item.orderNo,
        item.customerName,
        item.customerTaxId,
        item.branchName === '00000' ? item.branchName : '',
        item.branchName !== '00000' ? item.branchName : '',
        formatNumber(item.totalAmount, 2),
        (item.vatAmount && formatNumber(item.vatAmount, 2)) || '-',
      ]);

      // Apply alignment for each cell in the row
      row.eachCell((cell, colNumber) => {
        cell.alignment = {
          horizontal: [1, 2, 3, 5, 6, 7].includes(colNumber)
            ? 'center'
            : [8, 9].includes(colNumber)
              ? 'right'
              : 'left',
          vertical: 'middle',
          wrapText: true,
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });
    const totalAmount = sumBy(data || [], d => d.totalAmount);
    const totalTaxAmount = sumBy(data || [], d => d.vatAmount);
    worksheet.getRow((data?.length || 0) + 8).values = [
      '',
      '',
      '',
      '',
      '',
      '',
      'รวม',
      formatNumber(totalAmount, 2),
      (totalTaxAmount && formatNumber(totalTaxAmount, 2)) || '-',
    ];

    const row = worksheet.getRow((data?.length || 0) + 8);
    row.eachCell((cell, colNumber) => {
      cell.alignment = {
        horizontal: [7].includes(colNumber)
          ? 'center'
          : [8, 9].includes(colNumber)
            ? 'right'
            : 'left',
        vertical: 'middle',
        wrapText: true,
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Generate the Excel file and trigger the download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `tax-invoice-report-${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <div>
      <Button type="primary" ghost onClick={handleExport}>
        <ExportOutlined /> Export to Excel
      </Button>
    </div>
  );
};

export default ExcelExport;
